import { HttpContext, HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action } from "@ngrx/store";
import { Observable, of } from "rxjs";
import { catchError, exhaustMap, map, mergeMap, switchMap } from "rxjs/operators";
import { AllTierLimits, AuthenticationService, DemoCount, DemoModeService, DemoRetailer, LicenseService, Locale, PermissionsApi, ReportFtpSettings, ReportHttpSettings, ReportPreview, ReportS3Settings, ReportSftpSettings, ReportingService, RetailerLicenseState, TimeZone } from "src/app/generated/CrossPoint.Analytics.Server";
import { errorEventAction } from "../actions/app.actions";
import { Report } from '../../generated/CrossPoint.Analytics.Server/model/report';
import { AnalyticsActionsTypes, calculateReportRegenerationFailedAction, calculateReportRegenerationSuccessAction, createDemoRetailerFailedAction, createDemoRetailerSuccessAction, createReportFailedAction, createReportSuccessAction, deleteReportFailedAction, deleteReportSuccessAction, getAuthInfoAnalyticsFailedAction, getAuthInfoAnalyticsSuccessAction, getDemoStatisticsFailedAction, getDemoStatisticsSuccessAction, getLocalesFailedAction, getLocalesSuccessAction, getMatchingStoresFailedAction, getMatchingStoresSuccessAction, getReportsFailedAction, getReportsSuccessAction, getRetailerLicenseFailedAction, getRetailerLicenseSuccessAction, getTierLimitsFailedAction, getTierLimitsSuccessAction, getTimeZonesFailedAction, getTimeZonesSuccessAction, regenerateReportFailedAction, regenerateReportSuccessAction, testFtpSettingsFailedAction, testFtpSettingsSuccessAction, testHttpSettingsFailedAction, testHttpSettingsSuccessAction, testS3SettingsFailedAction, testS3SettingsSuccessAction, testSftpSettingsFailedAction, testSftpSettingsSuccessAction, updateReportFailedAction, updateReportSuccessAction } from "../actions/analytics.actions";
import { IDataAction } from "src/app/utils/interfaces/common.interfaces";
import { DetailedXpacInfo, GroupsService } from "src/app/generated/CrossPoint.RemoteXpac.Server";
import { HTTP_TIMEOUT_TOKEN } from "src/app/shared/interceptors/time-out-Interceptor";
import { ICalculateReportRegeneration, ICreateDemo, ICreateReport, IDeleteReport, IGetMatchingStores, IGetReports, IUpdateReport } from "src/app/utils/interfaces/analytics.interface";

const LONG_DURATION_TIMEOUT_MS: number = 3 * 60 * 1000;

@Injectable()
export class AnalyticsEffects {

  public getAuthInfoAnalyticsEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.GET_AUTH_INFO_ANALYTICS),
        exhaustMap(() => this.authenticationService.getPermissions()
          .pipe(
            map((permissions: PermissionsApi) => getAuthInfoAnalyticsSuccessAction({ data: permissions.permissions })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetAuthInfoAnalyticsErrorHasOccurred" } }), getAuthInfoAnalyticsFailedAction())
            })
          )
        )
      )
  );

  public getTierLimitsEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.GET_TIER_LIMITS),
        exhaustMap(() => this.licenseService.getTiers()
          .pipe(
            map((tiers: AllTierLimits) => getTierLimitsSuccessAction({ data: tiers })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetTierLimitsFailed" } }), getTierLimitsFailedAction())
            })
          )
        )
      )
  );

  public getRetailerLicenseEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.GET_RETAILER_LICENSE),
        exhaustMap(({ data: retailerId }) => {
          return this.licenseService.getLicenseStatus(retailerId)
            .pipe(
              map((license: RetailerLicenseState) => getRetailerLicenseSuccessAction({ data: license })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetRetailerLicenseFailed" } }), getRetailerLicenseFailedAction({ data: retailerId }))
              })
            );
        })
      )
  );

  public getReportsEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.GET_REPORTS_ANALYTICS),
        exhaustMap(({ data: { companyId } }: IDataAction<IGetReports>) => {
          return this.reportingService.getReports(companyId)
            .pipe(
              map((reports: Report[]) => getReportsSuccessAction({ data: reports })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetReportsErrorHasOccurred" } }), getReportsFailedAction())
              })
            );
        })
      )
  );

  public calculateReportRegenerationStoreEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.CALCULATE_REPORT_REGENERATION_ANALYTICS),
        exhaustMap(({ data: { reportId, from, until, companyId } }: IDataAction<ICalculateReportRegeneration>) => {
          // Calculating the regeneration can be expensive as it involves database lookups, so use the extra timeout
          const context = new HttpContext();
          context.set(HTTP_TIMEOUT_TOKEN, LONG_DURATION_TIMEOUT_MS);
          return this.reportingService.previewRegenerateReport(reportId, from, until, companyId, "body", false, {
              context: context,
            })
            .pipe(
              map((reportPreview: ReportPreview) => calculateReportRegenerationSuccessAction({ data: reportPreview })),
              catchError((error: HttpErrorResponse) => {
                return of(calculateReportRegenerationFailedAction({ data: error.error }))
              })
            );
        })
      )
  );

  public regenerateReportRegenerationStoreEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.REGENERATE_REPORT_ANALYTICS),
        exhaustMap(({ data: { reportId, from, until, companyId } }: IDataAction<ICalculateReportRegeneration>) => {
          // Regenerate is just a job dispatch, no need for an extended timeout
          return this.reportingService.regenerateReport(reportId, from, until, companyId)
            .pipe(
              map(() => regenerateReportSuccessAction()),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.RegenerateReportErrorHasOccurred" } }), regenerateReportFailedAction())
              })
            );
        })
      )
  );

  public getMatchingStoreEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.GET_MATCHING_STORES),
        exhaustMap(({ data: { companyId, companyDeviceFilter } }: IDataAction<IGetMatchingStores>) => {
          // Matching stores can be pretty slow, use an extended timeout
          const context = new HttpContext();
          context.set(HTTP_TIMEOUT_TOKEN, LONG_DURATION_TIMEOUT_MS);
          return this.groupsService.calculateGroupStores(companyId, null, companyDeviceFilter, "body", false, {
              context: context,
            })
            .pipe(
              map((reports: DetailedXpacInfo[]) => getMatchingStoresSuccessAction({ data: reports })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetMatchingStoresErrorHasOccurred" } }), getMatchingStoresFailedAction())
              })
            );
        })
      )
  );

  public createReportEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.CREATE_REPORT),
        exhaustMap(({ data: { companyId, report } }: IDataAction<ICreateReport>) =>
          this.reportingService.createReport(companyId, report)
            .pipe(
              map((report: Report) => createReportSuccessAction({ data: report })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.CreateReportFailed" } }), createReportFailedAction())
              })
            )
        )
      )
  );

  public updateReportEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.UPDATE_REPORT),
        exhaustMap(({ data: { reportId, companyId, report } }: IDataAction<IUpdateReport>) =>
          this.reportingService.updateReport(reportId, companyId, report)
            .pipe(
              map((report: Report) => updateReportSuccessAction({ data: report })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.UpdateReportFailed" } }), updateReportFailedAction())
              })
            )
        )
      )
  );


  public deleteReportEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.DELETE_REPORT),
        mergeMap(({ data: { companyId, reportId } }: IDataAction<IDeleteReport>) =>
          this.reportingService.deleteReport(reportId, companyId)
            .pipe(
              map(() => deleteReportSuccessAction({ data: { companyId, reportId } })),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.DeleteReportFailed" } }), deleteReportFailedAction())
              })
            )
        )
      )
  );

  public getTimeZonesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.GET_TIMEZONES_ANALYTICS),
        exhaustMap(() => this.reportingService.getTimeZones()
          .pipe(
            map((timeZones: TimeZone[]) => getTimeZonesSuccessAction({ data: timeZones })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetTimeZonesFailed" } }), getTimeZonesFailedAction())
            })
          )
        )
      )
  );

  public getDemoStatisticsEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.GET_DEMO_STATISTICS),
        switchMap(() => this.demoService.getDemoRetailerCount()
          .pipe(
            map((demoCount: DemoCount) => getDemoStatisticsSuccessAction({ data: demoCount })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetDemoStatisticsFailed" } }), getDemoStatisticsFailedAction())
            })
          )
        )
      )
  );

  public getLocalesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.GET_LOCALES),
        exhaustMap(() => this.reportingService.getLocales()
          .pipe(
            map((locales: Locale[]) => getLocalesSuccessAction({ data: locales })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetLocalesFailed" } }), getLocalesFailedAction())
            })
          )
        )
      )
  );

  public testFtpSettingsEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.TEST_FTP_SETTINGS),
        exhaustMap(({ data: settings }: IDataAction<ReportFtpSettings>) =>
          this.reportingService.testFtp(settings)
            .pipe(
              map(() => testFtpSettingsSuccessAction()),
              catchError((error: HttpErrorResponse) => {
                if (error.status === HttpStatusCode.NotAcceptable) {
                  // Expect result when the settings are wrong, so don't return an errorEventAction
                  const errorMessages: string[] = error.error;
                  return of(testFtpSettingsFailedAction({ data: errorMessages }));
                }
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.TestFtpSettingsFailed" } }), testFtpSettingsFailedAction({ data: [] }))
              })
            )
        )
      )
  );

  public testSftpSettingsEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.TEST_SFTP_SETTINGS),
        exhaustMap(({ data: settings }: IDataAction<ReportSftpSettings>) =>
          this.reportingService.testSftp(settings)
            .pipe(
              map(() => testSftpSettingsSuccessAction()),
              catchError((error: HttpErrorResponse) => {
                if (error.status === HttpStatusCode.NotAcceptable) {
                  // Expect result when the settings are wrong, so don't return an errorEventAction
                  const errorMessages: string[] = error.error;
                  return of(testSftpSettingsFailedAction({ data: errorMessages }));
                }
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.TestSftpSettingsFailed" } }), testSftpSettingsFailedAction({ data: [] }))
              })
            )
        )
      )
  );

  public testS3SettingsEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.TEST_S3_SETTINGS),
        exhaustMap(({ data: settings }: IDataAction<ReportS3Settings>) =>
          this.reportingService.testS3(settings)
            .pipe(
              map(() => testS3SettingsSuccessAction()),
              catchError((error: HttpErrorResponse) => {
                if (error.status === HttpStatusCode.NotAcceptable) {
                  // Expect result when the settings are wrong, so don't return an errorEventAction
                  const errorMessages: string[] = error.error;
                  return of(testS3SettingsFailedAction({ data: errorMessages }));
                }
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.TestS3SettingsFailed" } }), testS3SettingsFailedAction({ data: [] }))
              })
            )
        )
      )
  );

  public testHttpSettingsEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.TEST_HTTP_SETTINGS),
        exhaustMap(({ data: settings }: IDataAction<ReportHttpSettings>) =>
          this.reportingService.testHttp(settings)
            .pipe(
              map(() => testHttpSettingsSuccessAction()),
              catchError((error: HttpErrorResponse) => {
                if (error.status === HttpStatusCode.NotAcceptable) {
                  // Expect result when the settings are wrong, so don't return an errorEventAction
                  const errorMessages: string[] = error.error;
                  return of(testHttpSettingsFailedAction({ data: errorMessages }));
                }
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.TestHttpSettingsFailed" } }), testHttpSettingsFailedAction({ data: [] }))
              })
            )
        )
      )
  );

  public createDemoEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AnalyticsActionsTypes.CREATE_DEMO_RETAILER),
        exhaustMap(({ data }: IDataAction<ICreateDemo>) => this.demoService.createDemoRetailer(data.mailAddress, data.mailName)
          .pipe(
            map((retailer: DemoRetailer) => createDemoRetailerSuccessAction({ data: retailer })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({
                data: {
                  error: error,
                  translationkey: "StoreActionFailed.CreateDemoRetailerFailed",
                  translationErrorkey: "CreateDemoRetailerStatus",
                }
              }), createDemoRetailerFailedAction())
            })
          )
        )
      )
  );

  constructor(
    private actions$: Actions,
    private authenticationService: AuthenticationService,
    private reportingService: ReportingService,
    private groupsService: GroupsService,
    private licenseService: LicenseService,
    private demoService: DemoModeService,
  ) { }
}
